import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import { buildFilterParams } from '@Utils/buildFilterParams';
import { useWorkspaces } from '@Hooks/useWorkspaces';
import { Pencil, Percent, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import ConfirmationDialog from '@Components/ConfirmationDialog';
import service from '../../../service';
import { Workspace } from '../../../types/workspace';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@Contexts/auth';
import { useNavigate } from 'react-router-dom';
import { categoryIcons } from '@Constants/iconMap';

const WORKSPACE_FIELD_MAPPING: Record<string, string> = {
  name: 'filter[name]',
  description: 'filter[description]',
  color: 'filter[color]',
  icon: 'filter[icon]',
};

const columnConfigs: ColumnConfig<Workspace>[] = [
  {
    id: 'name',
    headerLabel: 'Name',
    accessor: (row) => row.name ?? '-',
    type: 'text',
  },
  {
    id: 'description',
    headerLabel: 'Description',
    accessor: (row) => row.description ?? '-',
    type: 'text',
    enableSorting: false,
  },
  {
    id: 'color',
    headerLabel: 'Color',
    accessor: (row) => row.color ?? '-',
    type: 'text',
    cell: (info) => {
      const value = info.getValue() as string;

      return (
        <div className="flex items-center gap-2">
          <div
            className="h-6 w-12 rounded border border-black"
            style={{
              backgroundColor: value || 'transparent',
            }}
          />
          {/* <span>{value || '-'}</span> */}
        </div>
      );
    },
    enableSorting: false,
  },
  {
    id: 'icon',
    headerLabel: 'Icon',
    accessor: (row) => row.icon ?? '',
    type: 'text',
    cell: (info) => {
      const iconKey = info.getValue() as string;
      const iconData = categoryIcons[iconKey];

      return (
        <div className="flex items-center gap-2">
          {iconData?.icon}
          {/* <span>{iconData?.label || iconKey || '-'}</span> */}
        </div>
      );
    },
    enableSorting: false,
  },
];

type WorkspacesListProps = {
  onEditClick?: (row: { original: Workspace }) => void;
  onCreateClick?: () => void;
};

export default function WorkspacesList({
  onEditClick,
  onCreateClick,
}: WorkspacesListProps = {}) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workspaceToDelete, setWorkspaceToDelete] = useState<Workspace | null>(
    null,
  );

  const navigate = useNavigate();

  const { user } = useAuth();

  const client = useQueryClient();

  const controller = useResourceTableController<Workspace>({
    columnsConfig: columnConfigs,
    defaultSort: {
      id: 'name',
      desc: false,
    },
    baseSearchParams: () => ({}),
    buildFilterParams: (filters) =>
      buildFilterParams(filters, {
        fieldMapping: WORKSPACE_FIELD_MAPPING,
      }),
    quickFilterFields: [
      { field: 'name', label: 'Name' },
      { field: 'description', label: 'Description' },
    ],
    initialPageSize: 10,
    rowActions: (row) => (
      <div className="flex gap-1">
        <Button
          variant="ghost"
          size="sm"
          // asChild
          onClick={(e) => {
            e.stopPropagation();
            onEditClick({ original: row });
          }}
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick(row);
          }}
        >
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    ),
  });

  const {
    data: workspaces,
    status,
    isError,
    error,
  } = useWorkspaces({ searchParams: controller.searchParams });

  useEffect(() => {
    if (isError && error) {
      toastError(
        error instanceof Error ? error.message : 'Failed to load Workspaces',
      );
    }
  }, [isError, error]);

  const rows: Workspace[] = workspaces?.data ?? [];
  const meta = workspaces?.meta;

  const handleDeleteClick = (workspace: Workspace) => {
    setWorkspaceToDelete(workspace);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!workspaceToDelete) return;

    try {
      await service.deleteWorkspace(workspaceToDelete.id, user?.id);

      toastSuccess(`"${workspaceToDelete.name}" has been deleted.`);
      setDeleteDialogOpen(false);
      setWorkspaceToDelete(null);
      client.invalidateQueries({ queryKey: ['workspaces'] });
    } catch (err) {
      toastError(
        err instanceof Error ? err.message : 'Failed to delete Workspace',
      );
    }
  };

  return (
    <div className="mt-2 space-y-4">
      <ResourceTableView<Workspace>
        label="Workspaces"
        controller={controller}
        data={rows}
        meta={meta}
        status={status}
        summaryIcon={<Percent className="h-4 w-4 text-zinc-500" />}
        summaryLabel="Total Workspaces: "
        renderExtraActions={() => (
          <Button
            variant="primary"
            onClick={(e) => {
              e.stopPropagation();
              onCreateClick();
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Create Workspace
          </Button>
        )}
        onRowClick={(row) => {
          navigate(`/workspaces/manager/${row.id}`);
        }}
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onSubmit={handleDeleteConfirm}
        match={workspaceToDelete?.name ?? ''}
        title="Delete Workspace"
        description={`Are you sure you want to delete "${workspaceToDelete?.name}"? This action cannot be undone.`}
        matchLabel="Type the workspace name to confirm"
        submitLabel="Delete"
        // isLoading={deleteMutation.isPending}
        variant="destructive"
      />
    </div>
  );
}
